import { DateTime } from 'luxon';
import { CONST_PAYMENT_RESERVED_TIMEOUT_MINUTES_WITH_SLACK } from './constants.js';
import { timeSiteNow } from './time.js';
var CouponState;
(function (CouponState) {
    CouponState["booked"] = "booked";
    CouponState["reserved"] = "reserved";
    CouponState["cancelled"] = "cancelled";
    CouponState["initial"] = "initial";
})(CouponState || (CouponState = {}));
const couponValidate = (coupon) => {
    if (coupon.state !== CouponState.booked) {
        return {
            isValid: false,
            message: 'Gavekortet finnes ikke eller er ikke aktivt enda.'
        };
    }
    if (coupon.initialValue <= 0) {
        return { isValid: false, message: 'Ingen verdi på gavekortet.' };
    }
    if (couponBalance(coupon) <= 0) {
        return { isValid: false, message: 'Gavekortet er oppbrukt.' };
    }
    const now = timeSiteNow();
    const validThrough = DateTime.fromISO(coupon.validThrough);
    const valid = now < validThrough;
    return { isValid: valid, message: valid ? '' : 'Gavekortet er utgått' };
};
const couponTransactionCountsAsApplied = (transaction, now) => {
    if (transaction.state === CouponState.booked)
        return true;
    if (transaction.state === CouponState.cancelled)
        return false;
    if (transaction.state === CouponState.initial)
        return false;
    if (transaction.state !== CouponState.reserved) {
        throw Error('Yo, unknown state here!');
    }
    const currentTime = now ?? timeSiteNow();
    const reservedAt = DateTime.fromISO(transaction.reservedAt);
    return (currentTime <=
        reservedAt.plus({
            minutes: CONST_PAYMENT_RESERVED_TIMEOUT_MINUTES_WITH_SLACK
        }));
};
const couponBalance = (coupon, now) => {
    const applied = coupon.applyHistory
        .filter((t) => couponTransactionCountsAsApplied(t, now))
        .map((transaction) => transaction.amount)
        .reduce((sum, current) => sum + current, 0);
    return coupon.initialValue - applied;
};
const couponFindTransactionByOrderId = (transactions, orderId) => {
    return transactions.find((transaction) => transaction.orderId === orderId);
};
const couponTransactionCreate = (orderId) => {
    return {
        orderId,
        state: CouponState.initial,
        amount: 0,
        reservedAt: '',
        reservedBy: '',
        bookedOrCancelledAt: '',
        bookedOrCancelledBy: ''
    };
};
const couponInnerReserve = (transactions, orderId, amount, uid, now) => {
    if (couponFindTransactionByOrderId(transactions, orderId)) {
        throw Error('Cannot reserve the coupon amount twice - already got an apply record with this orderId');
    }
    const transaction = couponTransactionCreate(orderId);
    transaction.state = CouponState.reserved;
    transaction.amount = amount;
    transaction.reservedBy = uid;
    transaction.reservedAt = now.toUTC().toISO() ?? DateTime.now().toISO();
    transactions.push(transaction);
};
const couponInnerBookOrCancel = (state, transactions, orderId, uid, now) => {
    const transaction = couponFindTransactionByOrderId(transactions, orderId);
    if (!transaction) {
        throw Error('Cannot find the coupon transaction to book - need a reserved coupon');
    }
    if (state !== CouponState.cancelled && state !== CouponState.booked) {
        throw Error('Cannot use this state here.');
    }
    transaction.bookedOrCancelledBy = uid;
    transaction.bookedOrCancelledAt = now.toUTC().toISO() || '';
    transaction.state = state;
};
const emptyCouponDoc = () => {
    return {
        initialValue: 0,
        state: CouponState.initial,
        validThrough: '',
        buyHistory: [],
        applyHistory: []
    };
};
const toCouponDoc = (data) => {
    const base = emptyCouponDoc();
    if (!data)
        return base;
    const dataCoersed = data;
    base.initialValue = Number(dataCoersed.initialValue) || 0;
    base.validThrough = dataCoersed.validThrough ?? base.validThrough;
    base.buyHistory = dataCoersed.buyHistory ?? [];
    // Correct some earlier string amounts
    base.buyHistory.forEach((transaction) => {
        transaction.amount = Number(transaction.amount) || 0;
    });
    base.applyHistory = dataCoersed.applyHistory ?? [];
    // Correct some earlier string amounts
    base.applyHistory.forEach((transaction) => {
        transaction.amount = Number(transaction.amount) || 0;
    });
    base.state = data.state ?? CouponState.initial;
    return base;
};
const couponBuyReserve = (coupon, orderId, amount, by, now, validDays) => {
    if (coupon.state !== CouponState.initial) {
        throw Error('Can only reserve a coupon if it is in the state initial. Was: ' +
            JSON.stringify(coupon));
    }
    couponInnerReserve(coupon.buyHistory, orderId, amount, by, now);
    coupon.state = CouponState.reserved;
    coupon.initialValue = amount;
    coupon.validThrough = now.plus({ days: validDays }).toUTC().toISO() ?? '';
};
const couponBuyBook = (coupon, orderId, by, now) => {
    couponInnerBookOrCancel(CouponState.booked, coupon.buyHistory, orderId, by, now);
    coupon.state = CouponState.booked;
};
const couponBuyCancel = (coupon, orderId, by, now) => {
    couponInnerBookOrCancel(CouponState.cancelled, coupon.buyHistory, orderId, by, now);
    coupon.state = CouponState.cancelled;
};
const couponApplyReserve = (coupon, orderId, amount, by, now) => {
    const applicable = couponValidate(coupon);
    if (!applicable.isValid)
        return applicable.message;
    if (couponBalance(coupon) < amount) {
        return 'Ikke nok verdi i dette gavekortet';
    }
    couponInnerReserve(coupon.applyHistory, orderId, amount, by, now);
    return '';
};
const couponApplyBook = (coupon, orderId, by, now) => {
    couponInnerBookOrCancel(CouponState.booked, coupon.applyHistory, orderId, by, now);
};
const couponApplyCancel = (coupon, orderId, by, now) => {
    couponInnerBookOrCancel(CouponState.cancelled, coupon.applyHistory, orderId, by, now);
};
//
// Coupon ids for the format
// hhh-hhh-hhh where h is a hex digit.
//
/**
 * Mint a gift-card id: `xxx-xxx-xxx-xxx`, 48 bits of CSPRNG, grouped in threes
 * so it can be read aloud and typed in (F-CRYPTO-01).
 *
 * **The id is the bearer credential.** Anyone holding it can look the card up
 * and spend its balance, so this generator is the whole of a gift card's
 * security. It used to be three `Math.random()` draws:
 *
 *     Math.floor(Math.random() * 0xfff)   // x3 -> 36 bits
 *
 * with a comment conceding that 36 bits was only defensible alongside a 500 ms
 * throttle "until we have API limiting" — a throttle that was never built, and
 * limiting that F-RATE-01b deliberately still does not apply to the lookup. Both
 * halves of that reasoning are now fixed rather than one:
 *
 * - **CSPRNG, not `Math.random`.** V8's `Math.random` is xorshift128+, whose
 *   internal state is recoverable from a modest run of outputs. Ids are minted
 *   server-side, so one Cloud Functions instance serves one stream — and
 *   `POST /coupon/:site` let any account draw from it on demand, which is
 *   precisely the sampling oracle such an attack needs. `getRandomValues` has no
 *   state to recover, so predicting one card from another is off the table
 *   whatever the mint rate.
 * - **48 bits, not 36.** ~281 trillion instead of ~68 billion, which puts brute
 *   force out of reach on its own rather than by leaning on a rate limit.
 *
 * `globalThis.crypto` rather than `node:crypto` because this module is
 * isomorphic; Web Crypto is present in both Node 22 and every browser this app
 * supports.
 *
 * Existing cards are unaffected — the id is stored, never re-derived — so the
 * two lengths coexist and only newly issued cards carry the fourth group.
 */
const COUPON_ID_BYTES = 6;
const couponRandomId = () => {
    const bytes = new Uint8Array(COUPON_ID_BYTES);
    globalThis.crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return (hex.match(/.{3}/g) ?? []).join('-');
};
export { emptyCouponDoc, toCouponDoc, couponBuyReserve, couponBuyBook, couponBuyCancel, couponApplyReserve, couponApplyBook, couponApplyCancel, CouponState, couponValidate, couponBalance, couponRandomId };
//# sourceMappingURL=coupon.js.map